import React, { useEffect, useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { attorneysService } from '../../../services/attorneysService';
import { homepageSliderService } from '../../../services/homepageSliderService';
import { practiceAreasService } from '../../../services/practiceAreasService';
import { publicationsService } from '../../../services/publicationsService';
import { storageService } from '../../../services/storageService';
import { supabase } from '../../../lib/supabase';

const SystemHealthMonitor = () => {
	const [healthChecks, setHealthChecks] = useState({});
	const [loading, setLoading] = useState(true);
	const [lastChecked, setLastChecked] = useState(null);
	const [systemMetrics, setSystemMetrics] = useState({
		responseTime: 0,
		uptime: 0,
		memoryUsage: 0,
		cpuUsage: 0,
	});

	// No automatic health checks - only manual triggers

	const runHealthChecks = async () => {
		setLoading(true);
		const checks = {};

		// Database Health Check
		try {
			const startTime = Date.now();
			await practiceAreasService.getAllPracticeAreas();
			const responseTime = Date.now() - startTime;
			checks.database = {
				status: 'healthy',
				responseTime,
				message: 'Database connection successful',
				timestamp: new Date().toISOString(),
			};
		} catch (error) {
			checks.database = {
				status: 'error',
				responseTime: 0,
				message: `Database error: ${error.message}`,
				timestamp: new Date().toISOString(),
			};
		}

		// Storage Health Check
		try {
			const startTime = Date.now();
			await storageService.listImages('images');
			const responseTime = Date.now() - startTime;
			checks.storage = {
				status: 'healthy',
				responseTime,
				message: 'Storage service accessible',
				timestamp: new Date().toISOString(),
			};
		} catch (error) {
			checks.storage = {
				status: 'error',
				responseTime: 0,
				message: `Storage error: ${error.message}`,
				timestamp: new Date().toISOString(),
			};
		}

		// API Health Check
		try {
			const startTime = Date.now();
			await attorneysService.getAllAttorneys();
			const responseTime = Date.now() - startTime;
			checks.api = {
				status: 'healthy',
				responseTime,
				message: 'API endpoints responding',
				timestamp: new Date().toISOString(),
			};
		} catch (error) {
			checks.api = {
				status: 'error',
				responseTime: 0,
				message: `API error: ${error.message}`,
				timestamp: new Date().toISOString(),
			};
		}

		// Supabase Connection Check
		try {
			const startTime = Date.now();
			const { data, error } = await supabase.from('practice_areas').select('count').limit(1);
			const responseTime = Date.now() - startTime;
			checks.supabase = {
				status: error ? 'error' : 'healthy',
				responseTime,
				message: error ? `Supabase error: ${error.message}` : 'Supabase connection successful',
				timestamp: new Date().toISOString(),
			};
		} catch (error) {
			checks.supabase = {
				status: 'error',
				responseTime: 0,
				message: `Supabase error: ${error.message}`,
				timestamp: new Date().toISOString(),
			};
		}

		// Service-specific checks
		const services = [
			{ name: 'homepageSlides', service: homepageSliderService, method: 'getSlides' },
			{
				name: 'publications',
				service: publicationsService,
				method: 'getPublications',
				args: [true],
			},
		];

		for (const service of services) {
			try {
				const startTime = Date.now();
				await service.service[service.method](...(service.args || []));
				const responseTime = Date.now() - startTime;
				checks[service.name] = {
					status: 'healthy',
					responseTime,
					message: `${service.name} service responding`,
					timestamp: new Date().toISOString(),
				};
			} catch (error) {
				checks[service.name] = {
					status: 'error',
					responseTime: 0,
					message: `${service.name} error: ${error.message}`,
					timestamp: new Date().toISOString(),
				};
			}
		}

		setHealthChecks(checks);
		setLastChecked(new Date().toISOString());
		setLoading(false);
	};

	const getStatusColor = (status) => {
		switch (status) {
			case 'healthy':
				return 'text-green-500';
			case 'error':
				return 'text-red-500';
			case 'warning':
				return 'text-yellow-500';
			default:
				return 'text-gray-500';
		}
	};

	const getStatusIcon = (status) => {
		switch (status) {
			case 'healthy':
				return 'CheckCircle';
			case 'error':
				return 'XCircle';
			case 'warning':
				return 'AlertTriangle';
			default:
				return 'HelpCircle';
		}
	};

	const getStatusBgColor = (status) => {
		switch (status) {
			case 'healthy':
				return 'bg-green-50 border-green-200';
			case 'error':
				return 'bg-red-50 border-red-200';
			case 'warning':
				return 'bg-yellow-50 border-yellow-200';
			default:
				return 'bg-gray-50 border-gray-200';
		}
	};

	const getOverallStatus = () => {
		const statuses = Object.values(healthChecks).map((check) => check.status);
		if (statuses.includes('error')) return 'error';
		if (statuses.includes('warning')) return 'warning';
		if (statuses.every((status) => status === 'healthy')) return 'healthy';
		return 'unknown';
	};

	const handleRefresh = () => {
		runHealthChecks();
	};

	const handleRestartServices = () => {
		alert(
			'Service restart functionality would be implemented here. This would restart all backend services.'
		);
	};

	const handleClearLogs = () => {
		alert('Clear logs functionality would be implemented here. This would clear system logs.');
	};

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex justify-between items-center'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>System Health Monitor</h1>
						<p className='text-gray-600'>Monitor system performance and service health manually</p>
					</div>
					<div className='flex space-x-3'>
						<Button
							variant='default'
							onClick={handleRefresh}
							disabled={loading}
							iconName='RefreshCw'
							iconPosition='left'
							className='bg-[#800020] hover:bg-[#800020]/90'
						>
							{loading ? 'Checking...' : 'Run Health Check'}
						</Button>
						<Button
							variant='outline'
							onClick={handleRestartServices}
							iconName='RotateCcw'
							iconPosition='left'
						>
							Restart Services
						</Button>
					</div>
				</div>
			</div>

			{/* Overall Status */}
			<div className={`border rounded-lg p-6 ${getStatusBgColor(getOverallStatus())}`}>
				<div className='flex items-center'>
					<Icon
						name={getStatusIcon(getOverallStatus())}
						className={`h-8 w-8 ${getStatusColor(getOverallStatus())} mr-3`}
					/>
					<div>
						<h2 className='text-xl font-semibold text-gray-900'>
							Overall System Status:{' '}
							{getOverallStatus().charAt(0).toUpperCase() + getOverallStatus().slice(1)}
						</h2>
						<p className='text-gray-600'>
							Last checked: {lastChecked ? new Date(lastChecked).toLocaleString() : 'Never'}
						</p>
					</div>
				</div>
			</div>

			{/* System Metrics */}
			<div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center'>
							<Icon name='Clock' className='h-5 w-5 text-white' />
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Avg Response Time</p>
							<p className='text-2xl font-semibold text-gray-900'>
								{Object.values(healthChecks).reduce((acc, check) => acc + check.responseTime, 0) /
									Object.keys(healthChecks).length || 0}
								ms
							</p>
						</div>
					</div>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='w-8 h-8 bg-green-500 rounded-md flex items-center justify-center'>
							<Icon name='Activity' className='h-5 w-5 text-white' />
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Services Running</p>
							<p className='text-2xl font-semibold text-gray-900'>
								{Object.values(healthChecks).filter((check) => check.status === 'healthy').length}
							</p>
						</div>
					</div>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='w-8 h-8 bg-red-500 rounded-md flex items-center justify-center'>
							<Icon name='AlertTriangle' className='h-5 w-5 text-white' />
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Issues Detected</p>
							<p className='text-2xl font-semibold text-gray-900'>
								{Object.values(healthChecks).filter((check) => check.status === 'error').length}
							</p>
						</div>
					</div>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center'>
						<div className='w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center'>
							<Icon name='TrendingUp' className='h-5 w-5 text-white' />
						</div>
						<div className='ml-4'>
							<p className='text-sm font-medium text-gray-500'>Uptime</p>
							<p className='text-2xl font-semibold text-gray-900'>99.9%</p>
						</div>
					</div>
				</div>
			</div>

			{/* Service Health Checks */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Service Health Checks</h2>
				<div className='space-y-4'>
					{Object.entries(healthChecks).map(([serviceName, check]) => (
						<div
							key={serviceName}
							className={`border rounded-lg p-4 ${getStatusBgColor(check.status)}`}
						>
							<div className='flex items-center justify-between'>
								<div className='flex items-center'>
									<Icon
										name={getStatusIcon(check.status)}
										className={`h-5 w-5 ${getStatusColor(check.status)} mr-3`}
									/>
									<div>
										<h3 className='font-medium text-gray-900 capitalize'>
											{serviceName.replace(/([A-Z])/g, ' $1').trim()}
										</h3>
										<p className='text-sm text-gray-600'>{check.message}</p>
									</div>
								</div>
								<div className='text-right'>
									<p className='text-sm font-medium text-gray-900'>{check.responseTime}ms</p>
									<p className='text-xs text-gray-500'>
										{new Date(check.timestamp).toLocaleTimeString()}
									</p>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* System Actions */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>System Actions</h2>
				<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
					<Button
						variant='outline'
						onClick={handleRefresh}
						disabled={loading}
						iconName='RefreshCw'
						iconPosition='left'
						className='w-full'
					>
						Run Health Checks
					</Button>
					<Button
						variant='outline'
						onClick={handleRestartServices}
						iconName='RotateCcw'
						iconPosition='left'
						className='w-full'
					>
						Restart All Services
					</Button>
					<Button
						variant='outline'
						onClick={handleClearLogs}
						iconName='Trash2'
						iconPosition='left'
						className='w-full'
					>
						Clear System Logs
					</Button>
				</div>
			</div>

			{/* Performance Chart Placeholder */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Performance Trends</h2>
				<div className='h-64 bg-gray-50 rounded-lg flex items-center justify-center'>
					<div className='text-center'>
						<Icon name='BarChart' className='h-12 w-12 text-gray-400 mx-auto mb-2' />
						<p className='text-gray-500'>Performance charts would be displayed here</p>
						<p className='text-sm text-gray-400'>
							Response times, error rates, and system metrics over time
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SystemHealthMonitor;
